import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  GitCompare, Loader2, ArrowRight, ExternalLink, CircleHelp, FileText,
} from "lucide-react";
import { toast } from "sonner";

const FACT_LABELS = {
  rent: "Rent", deposit: "Deposit", tenancy_dates: "Lease dates / term",
  landlord_contact: "Landlord contact", notice_address: "Notice address",
  appliances: "Included appliances", utilities: "Utilities",
  parking_storage: "Parking / storage", pets_guests: "Pets / guests",
  key_return: "Key return", renewal: "Renewal", bookmark: "Bookmark",
};
const flabel = (t) => FACT_LABELS[t] || t;
const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

function SourceLink({ source }) {
  if (!source?.doc_id) return null;
  return (
    <a href={`${API}/vault/${source.doc_id}/file`} target="_blank" rel="noreferrer"
      data-testid={`lr-source-${source.doc_id}-${source.page}`}
      className="inline-flex items-center gap-0.5 text-[11px] text-brand hover:underline">
      p.{source.page} <ExternalLink size={10} />
    </a>
  );
}

function Excerpt({ source }) {
  if (!source?.excerpt) return null;
  return <p className="text-[11px] italic text-white/45 truncate">“{source.excerpt}”</p>;
}

export function LeaseRenewalComparison({ propertyId, open, onOpenChange }) {
  const [candidates, setCandidates] = useState([]);
  const [baseline, setBaseline] = useState("");
  const [proposal, setProposal] = useState("");
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/properties/${propertyId}/lease-renewal/candidates`)
      .then(({ data }) => {
        const c = data.candidates || [];
        setCandidates(c);
        if (c.length >= 2) { setBaseline(c[0].doc_id); setProposal(c[c.length - 1].doc_id); }
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load documents."));
  }, [open, propertyId]);

  const runCompare = useCallback(async () => {
    if (!baseline || !proposal || baseline === proposal) { setCmp(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}/lease-renewal/compare`,
        { params: { baseline, proposal } });
      setCmp(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't compare those leases."); setCmp(null); }
    finally { setLoading(false); }
  }, [propertyId, baseline, proposal]);

  useEffect(() => { runCompare(); }, [runCompare]);

  const decide = async (m, relation) => {
    try {
      await api.post(`/properties/${propertyId}/lease-renewal/match`, {
        baseline_doc_id: baseline, baseline_fact_id: m.baseline.fact_id,
        proposal_doc_id: proposal, proposal_fact_id: m.proposal.fact_id, relation,
      });
      runCompare();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that decision."); }
  };

  const stateText = (c) => {
    switch (c.state) {
      case "unchanged": return { txt: "Unchanged", cls: "text-white/50" };
      case "amount_changed": return { txt: `Amount changed from ${money(c.from_amount)} to ${money(c.to_amount)}`, cls: "text-amber" };
      case "date_differs": return { txt: "Date differs", cls: "text-amber" };
      case "wording_differs": return { txt: "Wording differs — review both sources", cls: "text-amber" };
      default: return { txt: c.state, cls: "text-white/60" };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0F1B2D] text-white border-white/10" data-testid="lease-renewal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><GitCompare size={18} className="text-brand" /> Compare lease versions</DialogTitle>
          <DialogDescription className="text-white/55">
            A factual side-by-side of the confirmed facts you saved in each lease. TenantSeal doesn't interpret your lease or advise on any change.
          </DialogDescription>
        </DialogHeader>

        {candidates.length < 2 ? (
          <p className="text-sm text-white/60" data-testid="lr-need-two">
            Confirm Lease Navigator facts on at least two lease documents to compare them.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm">
              <select data-testid="lr-baseline" value={baseline} onChange={(e) => setBaseline(e.target.value)}
                className="flex-1 h-9 rounded-lg border border-white/15 bg-white/10 text-white text-sm px-2">
                {candidates.map((c) => <option key={c.doc_id} value={c.doc_id} className="text-black">{c.filename}</option>)}
              </select>
              <ArrowRight size={16} className="text-white/40 shrink-0" />
              <select data-testid="lr-proposal" value={proposal} onChange={(e) => setProposal(e.target.value)}
                className="flex-1 h-9 rounded-lg border border-white/15 bg-white/10 text-white text-sm px-2">
                {candidates.map((c) => <option key={c.doc_id} value={c.doc_id} className="text-black">{c.filename}</option>)}
              </select>
            </div>
            <div className="flex justify-between text-[11px] text-white/40 px-0.5">
              <span>Baseline</span><span>Proposed</span>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-white/60"><Loader2 className="animate-spin" size={16} /> Comparing…</div>
            ) : !cmp ? (
              <p className="text-sm text-white/45">Choose two different documents.</p>
            ) : (
              <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1" data-testid="lr-result">
                {cmp.changes.map((c, i) => {
                  const s = stateText(c);
                  return (
                    <div key={i} data-testid={`lr-change-${c.baseline.fact_type}`} className="rounded-xl border border-white/10 bg-white/5 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-white/70">{flabel(c.baseline.fact_type)}</span>
                        <span className={`text-[11px] font-semibold ${s.cls}`}>{s.txt}</span>
                      </div>
                      <div className="mt-1.5 grid grid-cols-2 gap-2 text-sm">
                        <div className="min-w-0">
                          <p className="text-white/85 break-words">{c.baseline.value}</p>
                          <Excerpt source={c.baseline.source} /><SourceLink source={c.baseline.source} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white/85 break-words">{c.proposal.value}</p>
                          <Excerpt source={c.proposal.source} /><SourceLink source={c.proposal.source} />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <OnlyGroup testid="lr-baseline-only" title="Present only in the baseline document" items={cmp.baseline_only} />
                <OnlyGroup testid="lr-proposal-only" title="Present only in the proposed document" items={cmp.proposal_only} />

                {cmp.possible_matches.length > 0 && (
                  <div className="rounded-xl border border-amber/40 bg-amber/10 p-3" data-testid="lr-possible">
                    <p className="flex items-center gap-1.5 text-xs font-bold text-amber"><CircleHelp size={14} /> Possible correspondence — confirmation required</p>
                    <p className="mt-1 text-[11px] text-amber/90">These share a fact type but aren't uniquely matched. Nothing is compared until you decide.</p>
                    <div className="mt-2 space-y-2">
                      {cmp.possible_matches.map((m, i) => (
                        <div key={i} data-testid={`lr-possible-${i}`} className="rounded-lg bg-white/5 border border-white/10 p-2 text-sm">
                          <p className="text-[11px] text-white/50">{flabel(m.fact_type)}</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="min-w-0"><p className="text-white/85 break-words">{m.baseline.value}</p><SourceLink source={m.baseline.source} /></div>
                            <div className="min-w-0"><p className="text-white/85 break-words">{m.proposal.value}</p><SourceLink source={m.proposal.source} /></div>
                          </div>
                          <div className="mt-1.5 flex gap-2">
                            <Button size="sm" className="h-7 rounded-lg text-xs" data-testid={`lr-same-${i}`} onClick={() => decide(m, "same")}>Same clause</Button>
                            <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs border-white/20 bg-transparent text-white" data-testid={`lr-diff-${i}`} onClick={() => decide(m, "different")}>Different</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {cmp.changes.length === 0 && cmp.baseline_only.length === 0 && cmp.proposal_only.length === 0 && cmp.possible_matches.length === 0 && (
                  <p className="text-sm text-white/45" data-testid="lr-empty">No confirmed facts to compare in these documents.</p>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function OnlyGroup({ title, items, testid }) {
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid={testid}>
      <p className="text-xs font-semibold text-white/70 flex items-center gap-1.5"><FileText size={13} /> {title}</p>
      <ul className="mt-1.5 space-y-1.5">
        {items.map((f) => (
          <li key={f.fact_id} className="text-sm">
            <span className="text-white/45 text-[11px]">{flabel(f.fact_type)}: </span>
            <span className="text-white/85">{f.value}</span> <SourceLink source={f.source} />
          </li>
        ))}
      </ul>
    </div>
  );
}
